
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface EmailVerificationOptions {
  onVerificationComplete?: () => void;
}

export function useEmailVerification(options: EmailVerificationOptions = {}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  
  const { 
    onVerificationComplete = () => {}
  } = options;

  const initiateEmailVerification = async (emailAddress: string) => {
    if (!emailAddress) return false;
    
    setLoading(true);
    
    try {
      // Validate the email basic format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailAddress)) {
        throw new Error('Please enter a valid email address');
      }
      
      // Store the email for verification
      setEmail(emailAddress);
      
      toast.success('Please check your email for a verification link');
      return true;
    } catch (error: any) {
      console.error("Email verification error:", error);
      toast.error(error.message || "Failed to send verification email");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<boolean> => {
    if (!email || !otp) {
      toast.error("Email address or verification code missing");
      return false;
    }
    
    setLoading(true);
    
    try {
      console.log(`Verifying OTP for email: ${email} with code: ${otp}`);
      
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (error) {
        console.error("Verification error:", error);
        throw error;
      }

      toast.success('Email verified successfully');
      onVerificationComplete();
      return true;
    } catch (error: any) {
      console.error("Email OTP verification error:", error);
      toast.error(error.message || "Failed to verify email");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    loading,
    initiateEmailVerification,
    verifyOtp,
    setEmail
  };
}
